import { useEffect, useState } from "react";
import { supabase } from "./supabase";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { useMobile } from "./hooks/useMobile";

import { SQL_PROBLEMS } from "./data/sqlProblems";
import { SQL_INTERMEDIATE_PROBLEMS } from "./data/sqlIntermediateProblems";
import { SQL_ADVANCED_PROBLEMS } from "./data/sqlAdvancedProblems";
import { SQL_INTERVIEW_PROBLEMS } from "./data/sqlInterviewProblems";
import { SQL_SCENARIOS_PROBLEMS } from "./data/sqlScenariosProblems";
import { SQL_COMPANY_PROBLEMS } from "./data/sqlCompanyProblems";
import { getCompanyProblemPath, getSubmissionProblemPath, getSubmissionKey } from "./data/sqlSearch";

import { useBadges } from "./badges/useBadges";
import BadgeUnlockModal from "./badges/BadgeUnlockModal";
import { getClosestBadges} from "./badges/badgeUtils";

import { usePageMeta } from "./hooks/usePageMeta";

import Navbar from "./Navbar"; // ← ADD THIS
// ─── CONSTANTS ────────────────────────────────────────────────────────────────

function getDailyChallenge() {
  const today = new Date();
  const seed = today.getFullYear() * 10000 + (today.getMonth() + 1) * 100 + today.getDate();


  // Combine intermediate + advanced as the pool
  const pool = [
    ...SQL_INTERMEDIATE_PROBLEMS.map(p => ({ ...p, category: "Intermediate", difficulty: "Medium", path: `/sql/intermediate/${p.id}` })),
    ...SQL_ADVANCED_PROBLEMS.map(p => ({ ...p, category: "Advanced", difficulty: "Hard", path: `/sql/advanced/${p.id}` })),
  ];

  // Pick one deterministically based on today's date
  const index = seed % pool.length;
  const picked = pool[index];

  return {
    id:          picked.id,
    title:       picked.title,
    category:    picked.category,
    difficulty:  picked.difficulty,
    description: picked.description,
    path:        picked.path,
    solvedToday: ((seed * 7) % 300) + 100, // fake but stable number per day
  };
}

const DAILY_CHALLENGE = getDailyChallenge();

const DIFF = {
  Easy:   { bg: "#f0fdf4", color: "#16a34a", border: "#bbf7d0" },
  Medium: { bg: "#fffbeb", color: "#d97706", border: "#fde68a" },
  Hard:   { bg: "#fef2f2", color: "#dc2626", border: "#fecaca" },
};

// const CATEGORY_PATH_MAP = {
//   sql_basics:        "/sql/basics",
//   sql_intermediate:  "/sql/intermediate",
//   sql_advanced:      "/sql/advanced",
//   sql_interview:     "/sql/interview",
//   sql_scenario:     "/sql/scenarios",
//   sql_scenarios:    "/sql/scenarios",
//   sql_company:      "/sql/company",
// };

// ─── HELPERS ─────────────────────────────────────────────────────────────────

function getInitials(name = "") {
  return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
}

function isThisWeek(dateStr) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const now = new Date();
  const weekAgo = new Date(now);
  weekAgo.setDate(now.getDate() - 7);
  return d >= weekAgo && d <= now;
}

function isLastWeek(dateStr) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const now = new Date();
  const twoWeeksAgo = new Date(now);
  twoWeeksAgo.setDate(now.getDate() - 14);
  const weekAgo = new Date(now);
  weekAgo.setDate(now.getDate() - 7);
  return d >= twoWeeksAgo && d < weekAgo;
}

// ─── SMALL UI COMPONENTS ─────────────────────────────────────────────────────

function DiffPill({ d }) {
  const s = DIFF[d] || DIFF.Easy;
  return (
    <span style={{ fontSize: "0.68rem", padding: "2px 8px", borderRadius: "10px", fontWeight: 600, background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>
      {d}
    </span>
  );
}

function CatPill({ cat }) {
  return (
    <span style={{ fontSize: "0.68rem", padding: "2px 8px", borderRadius: "10px", fontWeight: 600, background: "#eff6ff", color: "#2563eb", border: "1px solid #bfdbfe" }}>
      {cat}
    </span>
  );
}

function Card({ children, style = {}, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{ background: "#ffffff", border: "1.5px solid #e2e8f0", borderRadius: "14px", padding: "1.25rem 1.375rem", ...style, cursor: onClick ? "pointer" : "default" }}
      onMouseEnter={onClick ? (e) => (e.currentTarget.style.borderColor = "#bfdbfe") : undefined}
      onMouseLeave={onClick ? (e) => (e.currentTarget.style.borderColor = "#e2e8f0") : undefined}
    >
      {children}
    </div>
  );
}

function Section({ label, title, action, actionFn, children, style = {} }) {
  return (
    <div style={style}>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: "1rem" }}>
        <div>
          {label && <div style={{ fontSize: "0.68rem", color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "3px" }}>{label}</div>}
          {title && <h2 style={{ fontSize: "1.1rem", fontWeight: 800, letterSpacing: "-0.3px", margin: 0, color: "#0f172a" }}>{title}</h2>}
        </div>
        {action && (
          <button onClick={actionFn} style={{ fontSize: "0.78rem", color: "#2563eb", fontWeight: 600, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
            {action} →
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

// ─── NAV ─────────────────────────────────────────────────────────────────────

function Nav({ user, navigate, onSignOut, isMobile }) {
  // const [menuOpen, setMenuOpen] = useState(false);
  return (
    <Navbar />
    // <nav style={{ padding: isMobile ? "0.75rem 1rem" : "0.875rem 2.5rem", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", position: "sticky", top: 0, background: "rgba(255,255,255,0.97)", zIndex: 100 }}>
    //   <span onClick={() => navigate("/")} style={{ fontWeight: 800, fontSize: "1rem", color: "#0f172a", letterSpacing: "-0.3px", cursor: "pointer" }}>
    //   Repractiq
    //   </span>
    //   {isMobile ? (
    //     <button onClick={() => setMenuOpen(!menuOpen)} style={{ background: "none", border: "none", fontSize: "1.4rem", cursor: "pointer", color: "#0f172a" }}>
    //       {menuOpen ? "✕" : "☰"}
    //     </button>
    //   ) : (
    //     <div style={{ display: "flex", gap: "28px", alignItems: "center" }}>
    //       <span onClick={() => navigate("/home")}     style={{ fontSize: "0.85rem", color: "#2563eb", fontWeight: 600, cursor: "pointer", borderBottom: "2px solid #2563eb", paddingBottom: "2px" }}>Home</span>
    //       <span onClick={() => navigate("/sql")}      style={{ fontSize: "0.85rem", color: "#64748b", fontWeight: 600, cursor: "pointer" }}>Practice</span>
    //       <span onClick={() => navigate("/leaderboard")} style={{ fontSize: "0.85rem", color: "#64748b", fontWeight: 600, cursor: "pointer" }}>Leaderboard</span>
    //       <span onClick={() => navigate("/Blog")}  style={{ fontSize: "0.85rem", color: "#64748b", fontWeight: 600, cursor: "pointer" }}>Blog</span>
    //       <div
    //         onClick={() => navigate("/profile")}
    //         title={user?.fullName}
    //         style={{ width: "34px", height: "34px", borderRadius: "50%", background: "#eff6ff", border: "1.5px solid #bfdbfe", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: "0.75rem", color: "#2563eb", cursor: "pointer" }}
    //       >
    //         {getInitials(user?.fullName)}
    //       </div>
    //       <button
    //         onClick={onSignOut}
    //         style={{ fontSize: "0.78rem", color: "#64748b", background: "none", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "5px 12px", cursor: "pointer", fontWeight: 600 }}
    //       >
    //         Sign out
    //       </button>
    //     </div>
    //   )}
    //   {isMobile && menuOpen && (
    //     <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#ffffff", borderBottom: "1px solid #e2e8f0", padding: "0.5rem 0", zIndex: 200 }}>
    //       {[["Home", "/home"], ["Practice", "/sql"], ["Leaderboard", "/leaderboard"], ["Blog", "/Blog"], ["Profile", "/profile"]].map(([label, path]) => (
    //         <div key={label} onClick={() => { navigate(path); setMenuOpen(false); }} style={{ padding: "0.75rem 1.25rem", fontSize: "0.9rem", color: "#0f172a", fontWeight: 500, cursor: "pointer", borderBottom: "1px solid #f1f5f9" }}>
    //           {label}
    //         </div>
    //       ))}
    //       <div onClick={() => { onSignOut(); setMenuOpen(false); }} style={{ padding: "0.75rem 1.25rem", fontSize: "0.9rem", color: "#dc2626", fontWeight: 600, cursor: "pointer" }}>
    //         Sign out
    //       </div>
    //     </div>
    //   )}
    // </nav>
  );
}

// ─── HERO GREETING ────────────────────────────────────────────────────────────

function HeroGreeting({ user, navigate, isMobile }) {
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const weeklyDiff = (user.weeklyCount || 0) - (user.lastWeekCount || 0);
  const totalSQL =
    (SQL_PROBLEMS?.length || 0) +
    (SQL_INTERMEDIATE_PROBLEMS?.length || 0) +
    (SQL_ADVANCED_PROBLEMS?.length || 0) +
    (SQL_INTERVIEW_PROBLEMS?.length || 0) +
    (SQL_SCENARIOS_PROBLEMS?.length || 0) +
    (SQL_COMPANY_PROBLEMS?.length || 0);

  const accuracyPct = user.totalAttempts > 0
    ? Math.round((user.correctCount / user.totalAttempts) * 100)
    : 0;

  return (
    <div style={{ background: "linear-gradient(180deg, #eff6ff 0%, #ffffff 100%)", borderBottom: "1px solid #e2e8f0", padding: isMobile ? "1.25rem 1rem" : "2rem 2.5rem" }}>
      <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
        <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "stretch" : "flex-start", justifyContent: "space-between", gap: "1.5rem", flexWrap: "wrap" }}>
          <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "0.72rem", color: "#16a34a", background: "#f0fdf4", padding: "4px 12px", borderRadius: "20px", border: "1px solid #bbf7d0", marginBottom: "0.75rem", fontWeight: 600 }}>
              <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#16a34a", display: "inline-block" }} />
              🔥 {user.streak || 0}-day streak — keep it going!
            </div>
            <h1 style={{ fontSize: "clamp(1.4rem, 3vw, 1.9rem)", fontWeight: 800, letterSpacing: "-0.75px", margin: "0 0 0.5rem", color: "#0f172a" }}>
              {greeting}, {(user.fullName || "").split(" ")[0] || "there"} 👋
            </h1>
            <p style={{ fontSize: "0.88rem", color: "#64748b", margin: 0, lineHeight: 1.6 }}>
              {weeklyDiff > 0
                ? `You solved ${user.weeklyCount} problems this week — ${weeklyDiff} more than last week. Keep the momentum!`
                : user.weeklyCount > 0
                ? `You've solved ${user.weeklyCount} problems this week. Keep going!`
                : "No problems solved yet this week — let's change that today."}
            </p>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px", alignItems: isMobile ? "stretch" : "flex-end", width: isMobile ? "100%" : "auto" }}>
            <button
              onClick={() => navigate(user.lastProblem?.path || "/sql/basics")}
              style={{ padding: "12px 28px", borderRadius: "10px", background: "#2563eb", color: "#fff", fontWeight: 800, fontSize: "0.95rem", border: "none", cursor: "pointer", width: isMobile ? "100%" : "auto" }}
            >
              ▶ Continue Practice
            </button>
            <span style={{ fontSize: "0.72rem", color: "#94a3b8", textAlign: isMobile ? "center" : "right" }}>
              Last: {user.lastProblem?.title || "Start your first problem"}
            </span>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: "10px", marginTop: "1.5rem" }}>
          {[
            { label: "Problems solved",  val: user.solvedCount || 0,        sub: `of ${totalSQL} total` },
            { label: "Current streak",   val: `${user.streak || 0}d`,        sub: `Longest: ${user.longestStreak || 0}d` },
            { label: "Accuracy",         val: `${accuracyPct}%`,             sub: "correct on first try" },
            { label: "This week",        val: user.weeklyCount || 0,         sub: weeklyDiff >= 0 ? `+${weeklyDiff} vs last week` : `${weeklyDiff} vs last week` },
          ].map((s) => (
            <div key={s.label} style={{ background: "#ffffff", border: "1.5px solid #e2e8f0", borderRadius: "12px", padding: "0.875rem 1rem" }}>
              <div style={{ fontSize: "0.68rem", color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: "5px" }}>{s.label}</div>
              <div style={{ fontSize: "1.6rem", fontWeight: 800, letterSpacing: "-1px", color: "#0f172a", lineHeight: 1 }}>{s.val}</div>
              <div style={{ fontSize: "0.68rem", color: "#94a3b8", marginTop: "4px" }}>{s.sub}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── DAILY CHALLENGE ─────────────────────────────────────────────────────────

function DailyChallenge({ challenge, navigate, solvedIds }) {
  const alreadySolved = solvedIds.has(challenge.id);
  const [done, setDone] = useState(alreadySolved);

  return (
    <div style={{ background: "#0f172a", borderRadius: "14px", padding: "1.25rem 1.375rem", color: "#fff", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", right: "-20px", top: "-20px", width: "120px", height: "120px", borderRadius: "50%", background: "rgba(37,99,235,0.18)", pointerEvents: "none" }} />
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "0.75rem" }}>
        <span style={{ fontSize: "0.68rem", fontWeight: 700, color: "#fbbf24", background: "rgba(251,191,36,0.12)", border: "1px solid rgba(251,191,36,0.3)", borderRadius: "20px", padding: "3px 10px", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          🔥 Daily Challenge
        </span>
        <span style={{ fontSize: "0.68rem", color: "#64748b" }}>{challenge.solvedToday.toLocaleString()} solved today</span>
      </div>
      <div style={{ fontSize: "1rem", fontWeight: 800, color: "#ffffff", marginBottom: "0.375rem" }}>{challenge.title}</div>
      <div style={{ fontSize: "0.8rem", color: "#94a3b8", lineHeight: 1.6, marginBottom: "1rem", maxWidth: "340px" }}>{challenge.description}</div>
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <button
          onClick={() => { setDone(true); navigate(challenge.path); }}
          style={{ padding: "8px 20px", borderRadius: "8px", background: done ? "#16a34a" : "#2563eb", color: "#fff", fontWeight: 700, fontSize: "0.82rem", border: "none", cursor: "pointer" }}
        >
          {done ? "✓ Solved!" : "Solve today's challenge →"}
        </button>
        <DiffPill d={challenge.difficulty} />
        <CatPill cat={challenge.category} />
      </div>
    </div>
  );
}

// ─── CONTINUE CARD ────────────────────────────────────────────────────────────

function ContinueCard({ problem, navigate }) {
  return (
    <Card onClick={() => navigate(problem?.path || "/sql/basics")} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem" }}>
      <div>
        <div style={{ fontSize: "0.68rem", color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: "5px" }}>Resume where you left off</div>
        <div style={{ fontSize: "0.92rem", fontWeight: 700, color: "#0f172a", marginBottom: "6px" }}>{problem?.title || "Start your first problem"}</div>
        <div style={{ display: "flex", gap: "6px" }}>
          <CatPill cat={problem?.category || "Basics"} />
          <DiffPill d={problem?.difficulty || "Easy"} />
        </div>
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); navigate(problem?.path || "/sql/basics"); }}
        style={{ padding: "9px 20px", borderRadius: "8px", background: "#2563eb", color: "#fff", fontWeight: 700, fontSize: "0.82rem", border: "none", cursor: "pointer", whiteSpace: "nowrap" }}
      >
        Resume →
      </button>
    </Card>
  );
}

// ─── PROGRESS FEEDBACK ────────────────────────────────────────────────────────

function ProgressFeedback({ user }) {
  const weeklyDiff = (user.weeklyCount || 0) - (user.lastWeekCount || 0);
  const totalSQL =
    (SQL_PROBLEMS?.length || 0) +
    (SQL_INTERMEDIATE_PROBLEMS?.length || 0) +
    (SQL_ADVANCED_PROBLEMS?.length || 0) +
    (SQL_INTERVIEW_PROBLEMS?.length || 0) +
    (SQL_SCENARIOS_PROBLEMS?.length || 0) +
    (SQL_COMPANY_PROBLEMS?.length || 0);
  const pct = totalSQL > 0 ? Math.round(((user.solvedCount || 0) / totalSQL) * 100) : 0;

  // Simple XP estimate: 10 XP per correct solve
  const xp = (user.solvedCount || 0) * 10;
  const level = Math.floor(xp / 100) + 1;
  const xpInLevel = xp % 100;

  return (
    <Card style={{ background: "#f8fafc" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "1rem" }}>
        <div>
          <div style={{ fontSize: "0.68rem", color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "5px" }}>Your progress this week</div>
          <div style={{ fontSize: "1rem", fontWeight: 800, color: "#0f172a", marginBottom: "4px" }}>
            {weeklyDiff > 0
              ? `📈 Up ${weeklyDiff} problems vs last week`
              : weeklyDiff === 0
              ? "Steady pace — same as last week"
              : `📉 Down ${Math.abs(weeklyDiff)} vs last week`}
          </div>
          <div style={{ fontSize: "0.8rem", color: "#64748b" }}>
            {user.weeklyCount || 0} solved this week · {user.solvedCount || 0} total · {pct}% of SQL complete
          </div>
        </div>
        <div style={{ minWidth: "180px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.7rem", color: "#64748b", marginBottom: "4px" }}>
            <span>Level {level}</span>
            <span>{xpInLevel} / 100 XP</span>
          </div>
          <div style={{ height: "6px", background: "#e2e8f0", borderRadius: "3px", overflow: "hidden" }}>
            <div style={{ width: `${xpInLevel}%`, height: "100%", background: "#2563eb", borderRadius: "3px" }} />
          </div>
          <div style={{ fontSize: "0.65rem", color: "#94a3b8", marginTop: "4px", textAlign: "right" }}>
            {100 - xpInLevel} XP to Level {level + 1}
          </div>
        </div>
      </div>
    </Card>
  );
}

// ─── RECOMMENDED PROBLEMS ─────────────────────────────────────────────────────

function RecommendedProblems({ navigate, solvedIds, isMobile }) {
  const ALL_PROBLEMS = [
    ...SQL_PROBLEMS.map(p => ({ ...p, category: "sql_basics", difficulty: "Easy", path: `/sql/basics/${p.id}`, solvedKey: getSubmissionKey("sql_basics", p.id) })),
    ...SQL_INTERMEDIATE_PROBLEMS.map(p => ({ ...p, category: "sql_intermediate", difficulty: "Medium", path: `/sql/intermediate/${p.id}`, solvedKey: getSubmissionKey("sql_intermediate", p.id) })),
    ...SQL_ADVANCED_PROBLEMS.map(p => ({ ...p, category: "sql_advanced", difficulty: "Hard", path: `/sql/advanced/${p.id}`, solvedKey: getSubmissionKey("sql_advanced", p.id) })),
    ...SQL_INTERVIEW_PROBLEMS.map(p => ({ ...p, category: "sql_interview", difficulty: "Medium", path: `/sql/interview/${p.id}`, solvedKey: getSubmissionKey("sql_interview", p.id) })),
    ...SQL_SCENARIOS_PROBLEMS.map(p => ({ ...p, category: "sql_scenario", difficulty: "Hard", path: `/sql/scenarios/${p.id}`, solvedKey: getSubmissionKey("sql_scenario", p.id) })),
    ...SQL_COMPANY_PROBLEMS.map(p => ({ ...p, category: "sql_company", difficulty: p.difficulty || "Medium", path: getCompanyProblemPath(p), solvedKey: getSubmissionKey("sql_company", `${p.company}::${p.id}`) })),
  ];

  // Pick unsolved problems, prioritise basics first then others
  const unsolved = ALL_PROBLEMS.filter(p => !solvedIds.has(p.solvedKey || getSubmissionKey(p.category, p.id)));
  
  // Get next 4 unsolved — first from basics, then others
  const fromBasics = unsolved.filter(p => p.category === "sql_basics").slice(0, 2);
  const fromOthers = unsolved.filter(p => p.category !== "sql_basics").slice(0, 2);
  const list = [...fromBasics, ...fromOthers].slice(0, 4);

  const CATEGORY_LABEL = {
    sql_basics:       "Basics",
    sql_intermediate: "Intermediate",
    sql_advanced:     "Advanced",
    sql_interview:    "Interview",
    sql_scenario:     "Scenarios",
    sql_company:      "Company",
  };

  const REASON_MAP = {
    sql_basics:       "Next in sequence",
    sql_intermediate: "Level up",
    sql_advanced:     "Challenge yourself",
    sql_interview:    "Interview prep",
    sql_scenario:     "Real-world practice",
    sql_company:      "Top company prep",
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: "10px" }}>
      {list.map((p) => (
        <Card key={p.solvedKey || `${p.category}-${p.id}`} onClick={() => navigate(p.path)} style={{ padding: "1rem 1.125rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "6px" }}>
            <DiffPill d={p.difficulty} />
            <span style={{ fontSize: "0.65rem", color: "#94a3b8", background: "#f8fafc", padding: "2px 7px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
              {REASON_MAP[p.category]}
            </span>
          </div>
          <div style={{ fontSize: "0.85rem", fontWeight: 700, color: "#0f172a", margin: "6px 0 4px" }}>{p.title}</div>
          <div style={{ display: "flex", gap: "6px", alignItems: "center", marginBottom: "6px" }}>
            <CatPill cat={CATEGORY_LABEL[p.category]} />
          </div>
          <div style={{ fontSize: "0.72rem", color: "#2563eb", fontWeight: 600 }}>Solve this →</div>
        </Card>
      ))}
    </div>
  );
}

// ─── LEADERBOARD CARD ─────────────────────────────────────────────────────────
// Ranked by total solved + current streak combined (fetched live from Supabase)

function LeaderboardCard({ currentUser, leaderboard }) {
  const myRank = leaderboard.findIndex((u) => u.userId === currentUser.userId) + 1;

  return (
    <Card style={{ padding: "0" }}>
      <div style={{ padding: "1rem 1.375rem 0.75rem", borderBottom: "1px solid #f1f5f9" }}>
        <div style={{ fontSize: "0.68rem", color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>Top solvers (solved + streak)</div>
      </div>

      {leaderboard.slice(0, 5).map((u, i) => {
        const badge = i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : "";
        const isMe = u.userId === currentUser.userId;
        return (
          <div key={u.userId} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "0.7rem 1.375rem", borderBottom: i < 4 ? "1px solid #f1f5f9" : "none", background: isMe ? "#eff6ff" : "transparent" }}>
            <div style={{ width: "22px", textAlign: "center", fontSize: "0.82rem" }}>
              {badge || <span style={{ color: "#94a3b8", fontSize: "0.72rem" }}>#{i + 1}</span>}
            </div>
            <div style={{ width: "30px", height: "30px", borderRadius: "50%", background: isMe ? "#2563eb" : "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.65rem", fontWeight: 700, color: isMe ? "#fff" : "#2563eb", flexShrink: 0 }}>
              {getInitials(u.fullName)}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: "0.82rem", fontWeight: 600, color: "#0f172a" }}>{isMe ? "You" : u.fullName}</div>
              <div style={{ fontSize: "0.68rem", color: "#94a3b8" }}>🔥 {u.streak}d streak</div>
            </div>
            <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "#2563eb" }}>{u.solved} solved</div>
          </div>
        );
      })}

      {/* Current user row if not in top 5 */}
      {myRank > 5 && (
        <div style={{ margin: "0", padding: "0.75rem 1.375rem", background: "#eff6ff", borderTop: "1.5px solid #bfdbfe", borderRadius: "0 0 12px 12px", display: "flex", alignItems: "center", gap: "10px" }}>
          <div style={{ width: "22px", textAlign: "center", fontSize: "0.75rem", color: "#2563eb", fontWeight: 700 }}>#{myRank}</div>
          <div style={{ width: "30px", height: "30px", borderRadius: "50%", background: "#2563eb", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.65rem", fontWeight: 700, color: "#fff", flexShrink: 0 }}>
            {getInitials(currentUser.fullName)}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: "0.82rem", fontWeight: 700, color: "#1d4ed8" }}>You</div>
            <div style={{ fontSize: "0.68rem", color: "#60a5fa" }}>🔥 {currentUser.streak}d streak</div>
          </div>
          <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "#1d4ed8" }}>{currentUser.solvedCount || 0} solved</div>
        </div>
      )}

      {leaderboard.length === 0 && (
        <div style={{ padding: "1.5rem", textAlign: "center", fontSize: "0.82rem", color: "#94a3b8" }}>
          No data yet — be the first on the board!
        </div>
      )}
    </Card>
  );
}

// ─── COMMUNITY FEED ───────────────────────────────────────────────────────────
// Shows the 6 most recent correct submissions across all users

function CommunityFeed({ feed }) {
  if (feed.length === 0) {
    return (
      <Card style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "160px" }}>
        <span style={{ fontSize: "0.82rem", color: "#94a3b8" }}>No activity yet — start solving!</span>
      </Card>
    );
  }

  function timeAgo(dateStr) {
    // Force UTC parsing — Supabase sometimes returns without Z suffix
    const utcStr = dateStr.endsWith("Z") ? dateStr : dateStr + "Z";
    const diff = Math.floor((Date.now() - new Date(utcStr)) / 1000);
    if (diff < 60)    return `${diff}s ago`;
    if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  }

  return (
    <Card style={{ padding: "0" }}>
      <div style={{ padding: "1rem 1.375rem 0.75rem", borderBottom: "1px solid #f1f5f9" }}>
        <div style={{ fontSize: "0.68rem", color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>Community — solving right now</div>
      </div>
      {feed.map((item, i) => (
        <div key={item.id} style={{ display: "flex", alignItems: "flex-start", gap: "10px", padding: "0.75rem 1.375rem", borderBottom: i < feed.length - 1 ? "1px solid #f1f5f9" : "none" }}>
          <div style={{ width: "30px", height: "30px", borderRadius: "50%", background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.65rem", fontWeight: 700, color: "#2563eb", flexShrink: 0 }}>
            {getInitials(item.fullName)}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: "0.8rem", color: "#0f172a" }}>
            <div style={{
  fontSize: "0.82rem",
  color: "#0f172a"
}}>
🔥 <strong>{item.fullName}</strong>{" "}
solved{" "}
<strong>{item.problem_title}</strong>
</div>

<div style={{
  fontSize: "0.7rem",
  color: "#64748b",
  marginTop: "3px"
}}>
  {item.streak} day streak
</div>
            </div>
            <div style={{ display: "flex", gap: "6px", alignItems: "center", marginTop: "4px" }}>
              <CatPill cat={item.category.replace("sql_", "").replace(/^\w/, (c) => c.toUpperCase())} />
              <span style={{ fontSize: "0.65rem", color: "#94a3b8" }}>{timeAgo(item.created_at)}</span>
            </div>
          </div>
        </div>
      ))}
    </Card>
  );
}

// ─── BADGES ──────────────────────────────────────────────────────────────────

function BadgesCard({ user, badges, navigate }) {
  const closest = getClosestBadges(badges.stats, badges.earnedIds);

  return (
    <Card style={{ background: "#f8fafc", display: "flex", flexDirection: "column", gap: "0.75rem" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: "0.68rem", color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Badges & Certificates
        </div>
        <button
          onClick={() => navigate("/profile")}
          style={{ fontSize: "0.68rem", color: "#2563eb", fontWeight: 600, background: "none", border: "none", cursor: "pointer", padding: 0 }}
        >
          View all →
        </button>
      </div>

      {/* Earned badges row — clickable, goes to profile badges tab */}
      {badges.earnedBadges.length > 0 ? (
        <div>
          <div style={{ fontSize: "0.68rem", color: "#94a3b8", marginBottom: "6px" }}>
            {badges.earnedIds.length} earned — click to view & share
          </div>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            {badges.earnedBadges.slice(0, 6).map((b) => (
              <div
                key={b.id}
                title={b.title}
                onClick={() => navigate("/profile")}
                style={{
                  fontSize: "1.6rem", cursor: "pointer",
                  background: "#ffffff", border: "1.5px solid #e2e8f0",
                  borderRadius: "10px", padding: "8px 10px",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  width: "48px", height: "48px",
                  transition: "border-color 0.15s",
                }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = "#bfdbfe"}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = "#e2e8f0"}
              >
                {b.icon}
              </div>
            ))}
            {badges.earnedBadges.length > 6 && (
              <div
                onClick={() => navigate("/profile")}
                style={{ fontSize: "0.72rem", color: "#2563eb", alignSelf: "center", cursor: "pointer", fontWeight: 600 }}
              >
                +{badges.earnedBadges.length - 6} more →
              </div>
            )}
          </div>
        </div>
      ) : (
        <div style={{ fontSize: "0.8rem", color: "#94a3b8" }}>
          Solve problems to earn your first badge!
        </div>
      )}

      {/* Divider */}
      {closest.length > 0 && (
        <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: "0.5rem" }}>
          <div style={{ fontSize: "0.68rem", color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "0.5rem" }}>
            Next to unlock
          </div>
          {closest.map(({ badge, remaining, progress }) => {
            // Map badge category to the right practice path
            const badgePath = badge.category
              ? {
                  sql_basics:       "/sql/basics",
                  sql_intermediate: "/sql/intermediate",
                  sql_advanced:     "/sql/advanced",
                  sql_interview:    "/sql/interview",
                  sql_scenario:     "/sql/scenarios",
                  sql_company:      "/sql/company",
                }[badge.category] || "/sql"
              : "/sql";

            return (
              <div
                key={badge.id}
                onClick={() => navigate(badgePath)}
                style={{ marginBottom:"0.5rem", cursor: "pointer", padding: "0.5rem 0.625rem", borderRadius: "8px", background: "#ffffff", border: "1px solid #e2e8f0", transition: "border-color 0.15s" }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = "#bfdbfe"}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = "#e2e8f0"}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
                  <span style={{ fontSize: "1rem" }}>{badge.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "0.8rem", fontWeight: 700, color: "#0f172a" }}>{badge.title}</div>
                    <div style={{ fontSize: "0.68rem", color: "#94a3b8" }}>
                      {remaining} more problems → unlock this badge
                    </div>
                  </div>
                  <span style={{ fontSize: "0.65rem", color: "#2563eb", fontWeight: 600 }}>
                    Practice →
                  </span>
                </div>
                <div style={{ height: "5px", background: "#e2e8f0", borderRadius: "2px", overflow: "hidden" }}>
                  <div style={{ width: `${progress}%`, height: "100%", background: progress === 100 ? "#16a34a" : "#2563eb", borderRadius: "2px", transition: "width 0.4s ease" }} />
                </div>
                <div style={{ fontSize: "0.62rem", color: "#94a3b8", marginTop: "2px" }}>
                  {progress}% complete
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Certificate teaser if any earned */}
      {badges.certificateBadges?.length > 0 && (
        <div
          onClick={() => navigate("/profile")}
          style={{ background: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)", border: "1px solid #fde68a", borderRadius: "8px", padding: "0.5rem 0.75rem", cursor: "pointer", display: "flex", alignItems: "center", gap: "10px" }}
        >
          <span style={{ fontSize: "1.25rem" }}>🎓</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: "0.8rem", fontWeight: 700, color: "#92400e" }}>
              {badges.certificateBadges.length} certificate{badges.certificateBadges.length > 1 ? "s" : ""} available
            </div>
            <div style={{ fontSize: "0.68rem", color: "#b45309" }}>
              Download & share on LinkedIn →
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

// ─── PRACTICE CATEGORIES ──────────────────────────────────────────────────────

function PracticeCategories({ navigate, sqlCounts, isMobile }) {
  const categories = [
    { key: "sql",       label: "SQL",         sublabel: "Basics → Advanced",    color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe", total: sqlCounts,                           path: "/sql",           active: true },
    { key: "python",    label: "Python",       sublabel: "Coming soon",           color: "#94a3b8", bg: "#f8fafc", border: "#e2e8f0", total: null,                                path: null,             active: false },
    { key: "java",      label: "Java",         sublabel: "Coming soon",           color: "#94a3b8", bg: "#f8fafc", border: "#e2e8f0", total: null,                                path: null,             active: false },
    { key: "scenarios", label: "Real-world",   sublabel: "Business scenarios",    color: "#0f172a", bg: "#f8fafc", border: "#e2e8f0", total: SQL_SCENARIOS_PROBLEMS?.length || 0, path: "/sql/scenarios", active: true },
  ];

  return (
    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: "10px" }}>
      {categories.map((cat) => (
        <div
          key={cat.key}
          onClick={() => cat.active && cat.path && navigate(cat.path)}
          style={{ background: cat.bg, border: `1.5px solid ${cat.border}`, borderRadius: "12px", padding: "1.125rem", cursor: cat.active ? "pointer" : "default", opacity: cat.active ? 1 : 0.6 }}
          onMouseEnter={cat.active ? (e) => (e.currentTarget.style.borderColor = "#93c5fd") : undefined}
          onMouseLeave={cat.active ? (e) => (e.currentTarget.style.borderColor = cat.border) : undefined}
        >
          <div style={{ fontSize: "0.78rem", fontWeight: 800, color: cat.color, marginBottom: "3px" }}>{cat.label}</div>
          <div style={{ fontSize: "0.7rem", color: "#94a3b8", marginBottom: "8px" }}>{cat.sublabel}</div>
          {cat.total !== null
            ? <div style={{ fontSize: "0.68rem", color: cat.color, fontWeight: 600, background: "#fff", padding: "2px 8px", borderRadius: "10px", border: `1px solid ${cat.border}`, width: "fit-content" }}>{cat.total}+ problems</div>
            : <div style={{ fontSize: "0.68rem", color: "#94a3b8", fontWeight: 500 }}>Coming soon</div>
          }
        </div>
      ))}
    </div>
  );
}

// ─── EMPTY STATE ──────────────────────────────────────────────────────────────

function EmptyState({ navigate }) {
  return (
    <div style={{ textAlign: "center", padding: "3rem 2rem", background: "#f8fafc", border: "1.5px dashed #e2e8f0", borderRadius: "14px" }}>
      <div style={{ fontSize: "2rem", marginBottom: "0.75rem" }}>🚀</div>
      <div style={{ fontSize: "1.1rem", fontWeight: 800, color: "#0f172a", marginBottom: "0.5rem" }}>Start your first problem</div>
      <div style={{ fontSize: "0.85rem", color: "#64748b", lineHeight: 1.7, marginBottom: "1.5rem", maxWidth: "360px", margin: "0 auto 1.5rem" }}>
        You haven't solved anything yet — that's fine. Pick a category and write your first SQL query.
      </div>
      <button
        onClick={() => navigate("/sql/basics")}
        style={{ padding: "10px 24px", borderRadius: "8px", background: "#2563eb", color: "#fff", fontWeight: 700, fontSize: "0.88rem", border: "none", cursor: "pointer" }}
      >
        Start SQL Basics →
      </button>
    </div>
  );
}

// ─── ONBOARDING MODAL ─────────────────────────────────────────────────────────
const ONBOARDING_QUESTIONS = [
  {
    key: "tool",
    title: "Which tool do you want to learn?",
    options: [
      { 
        value: "sql", 
        label: "SQL", 
        sub: "Master database queries, joins, window functions, and data analytics" 
      },
      { 
        value: "python", 
        label: "Python", 
        sub: "Learn programming basics, data structures, and data manipulation" 
      },
    ],
  },
  {
    key: "goal",
    title: "What's your goal?",
    options: [
      { value: "job",       label: "Get a data analyst / engineer job" },
      { value: "improve",   label: "Improve at my current job" },
      { value: "interview", label: "Crack a technical interview" },
      { value: "fun",       label: "Just learning for fun" },
    ],
  },
];

// ─── ONBOARDING MODAL COMPONENT ─────────────────────────────────────────────

function OnboardingModal({ step, answers, setAnswers, setStep, onFinish, isMobile }) {
  const navigate = useNavigate();
  const question = ONBOARDING_QUESTIONS[step];
  const selected = answers[question.key];

  const handleSelect = (value) => {
    setAnswers((prev) => ({ ...prev, [question.key]: value }));
  };

  const handleContinue = () => {
    if (step < ONBOARDING_QUESTIONS.length - 1) {
      setStep(step + 1);
    } else {
      // Complete answers object
      const finalAnswers = { ...answers };
      
      // Call parent onFinish handler if provided
      if (onFinish) {
        onFinish(finalAnswers);
      }

      // Route based on selected tool
      if (finalAnswers.tool === "python") {
        navigate("/python"); // Adjust path to your Python landing page route
      } else {
        navigate("/sql");    // Adjust path to your SQL landing page route
      }
    }
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2000,
        padding: isMobile ? "1rem" : "2rem",
      }}
    >
      <div
        style={{
          background: "#ffffff",
          borderRadius: "20px",
          padding: isMobile ? "1.5rem" : "2.5rem",
          width: "100%",
          maxWidth: "480px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
        }}
      >
        {/* Progress bar */}
        <div style={{ display: "flex", gap: "6px", marginBottom: "1.5rem" }}>
          {ONBOARDING_QUESTIONS.map((_, i) => (
            <div
              key={i}
              style={{
                flex: 1,
                height: "4px",
                borderRadius: "2px",
                background: i <= step ? "#2563eb" : "#e2e8f0",
                transition: "background 0.3s ease",
              }}
            />
          ))}
        </div>

        <div
          style={{
            fontSize: "0.7rem",
            color: "#94a3b8",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            marginBottom: "0.5rem",
          }}
        >
          Question {step + 1} of {ONBOARDING_QUESTIONS.length}
        </div>

        <h2
          style={{
            fontSize: "1.3rem",
            fontWeight: 800,
            color: "#0f172a",
            letterSpacing: "-0.5px",
            margin: "0 0 1.5rem",
          }}
        >
          {question.title}
        </h2>

        {/* Options list */}
        <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "2rem" }}>
          {question.options.map((opt) => {
            const isSelected = selected === opt.value;
            return (
              <div
                key={opt.value}
                onClick={() => handleSelect(opt.value)}
                style={{
                  padding: "0.875rem 1rem",
                  borderRadius: "10px",
                  border: `1.5px solid ${isSelected ? "#2563eb" : "#e2e8f0"}`,
                  background: isSelected ? "#eff6ff" : "#ffffff",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  transition: "all 0.15s ease",
                }}
              >
                <div
                  style={{
                    width: "18px",
                    height: "18px",
                    borderRadius: "50%",
                    border: `2px solid ${isSelected ? "#2563eb" : "#cbd5e1"}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {isSelected && (
                    <div
                      style={{
                        width: "10px",
                        height: "10px",
                        borderRadius: "50%",
                        background: "#2563eb",
                      }}
                    />
                  )}
                </div>
                <div>
                  <div style={{ fontSize: "0.88rem", fontWeight: 600, color: "#0f172a" }}>
                    {opt.label}
                  </div>
                  {opt.sub && (
                    <div style={{ fontSize: "0.75rem", color: "#94a3b8", marginTop: "2px" }}>
                      {opt.sub}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: "10px", justifyContent: "space-between" }}>
          {step > 0 ? (
            <button
              onClick={handleBack}
              style={{
                padding: "10px 20px",
                borderRadius: "8px",
                border: "1.5px solid #e2e8f0",
                background: "#fff",
                color: "#64748b",
                fontWeight: 600,
                fontSize: "0.85rem",
                cursor: "pointer",
              }}
            >
              ← Back
            </button>
          ) : (
            <div />
          )}

          <button
            onClick={handleContinue}
            disabled={!selected}
            style={{
              padding: "10px 28px",
              borderRadius: "8px",
              border: "none",
              background: selected ? "#2563eb" : "#cbd5e1",
              color: "#fff",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: selected ? "pointer" : "not-allowed",
            }}
          >
            {step === ONBOARDING_QUESTIONS.length - 1 ? "Get Started →" : "Continue →"}
          </button>
        </div>
      </div>
    </div>
  );
}

// const ONBOARDING_QUESTIONS = [
//   {
//     key: "experience",
//     title: "What's your SQL experience?",
//     options: [
//       { value: "beginner", label: "Complete beginner", sub: "I've never written SQL before" },
//       { value: "basics",   label: "Know the basics", sub: "Comfortable with SELECT, WHERE" },
//       { value: "joins",    label: "Comfortable with JOINs and aggregations", sub: "Can combine tables and group data" },
//       { value: "advanced", label: "Advanced", sub: "Window functions, CTEs, optimization" },
//     ],
//   },
//   {
//     key: "goal",
//     title: "What's your goal?",
//     options: [
//       { value: "job",       label: "Get a data analyst job" },
//       { value: "improve",   label: "Improve at my current job" },
//       { value: "interview", label: "Crack a technical interview" },
//       { value: "fun",       label: "Just learning for fun" },
//     ],
//   },
//   {
//     key: "time",
//     title: "How much time per day?",
//     options: [
//       { value: "15min", label: "15 minutes" },
//       { value: "30min", label: "30 minutes" },
//       { value: "1hour", label: "1 hour+" },
//     ],
//   },
// ];

// function OnboardingModal({ step, answers, setAnswers, setStep, onFinish, isMobile }) {
//   const question = ONBOARDING_QUESTIONS[step];
//   const selected  = answers[question.key];

//   const handleSelect = (value) => {
//     setAnswers((prev) => ({ ...prev, [question.key]: value }));
//   };

//   const handleContinue = () => {
//     if (step < ONBOARDING_QUESTIONS.length - 1) {
//       setStep(step + 1);
//     } else {
//       onFinish({ ...answers });
//     }
//   };

//   const handleBack = () => {
//     if (step > 0) setStep(step - 1);
//   };

//   return (
//     <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000, padding: isMobile ? "1rem" : "2rem" }}>
//       <div style={{ background: "#ffffff", borderRadius: "20px", padding: isMobile ? "1.5rem" : "2.5rem", width: "100%", maxWidth: "480px", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>

//         {/* Progress bar */}
//         <div style={{ display: "flex", gap: "6px", marginBottom: "1.5rem" }}>
//           {ONBOARDING_QUESTIONS.map((_, i) => (
//             <div key={i} style={{ flex: 1, height: "4px", borderRadius: "2px", background: i <= step ? "#2563eb" : "#e2e8f0" }} />
//           ))}
//         </div>

//         <div style={{ fontSize: "0.7rem", color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "0.5rem" }}>
//           Question {step + 1} of {ONBOARDING_QUESTIONS.length}
//         </div>

//         <h2 style={{ fontSize: "1.3rem", fontWeight: 800, color: "#0f172a", letterSpacing: "-0.5px", margin: "0 0 1.5rem" }}>
//           {question.title}
//         </h2>

//         <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "2rem" }}>
//           {question.options.map((opt) => {
//             const isSelected = selected === opt.value;
//             return (
//               <div
//                 key={opt.value}
//                 onClick={() => handleSelect(opt.value)}
//                 style={{
//                   padding: "0.875rem 1rem",
//                   borderRadius: "10px",
//                   border: `1.5px solid ${isSelected ? "#2563eb" : "#e2e8f0"}`,
//                   background: isSelected ? "#eff6ff" : "#ffffff",
//                   cursor: "pointer",
//                   display: "flex",
//                   alignItems: "center",
//                   gap: "10px",
//                   transition: "all 0.15s",
//                 }}
//               >
//                 <div style={{
//                   width: "18px", height: "18px", borderRadius: "50%",
//                   border: `2px solid ${isSelected ? "#2563eb" : "#cbd5e1"}`,
//                   display: "flex", alignItems: "center", justifyContent: "center",
//                   flexShrink: 0,
//                 }}>
//                   {isSelected && <div style={{ width: "10px", height: "10px", borderRadius: "50%", background: "#2563eb" }} />}
//                 </div>
//                 <div>
//                   <div style={{ fontSize: "0.88rem", fontWeight: 600, color: "#0f172a" }}>{opt.label}</div>
//                   {opt.sub && <div style={{ fontSize: "0.75rem", color: "#94a3b8", marginTop: "2px" }}>{opt.sub}</div>}
//                 </div>
//               </div>
//             );
//           })}
//         </div>

//         <div style={{ display: "flex", gap: "10px", justifyContent: "space-between" }}>
//           {step > 0 ? (
//             <button onClick={handleBack} style={{ padding: "10px 20px", borderRadius: "8px", border: "1.5px solid #e2e8f0", background: "#fff", color: "#64748b", fontWeight: 600, fontSize: "0.85rem", cursor: "pointer" }}>
//               ← Back
//             </button>
//           ) : <div />}
//           <button
//             onClick={handleContinue}
//             disabled={!selected}
//             style={{
//               padding: "10px 28px", borderRadius: "8px", border: "none",
//               background: selected ? "#2563eb" : "#cbd5e1",
//               color: "#fff", fontWeight: 700, fontSize: "0.85rem",
//               cursor: selected ? "pointer" : "not-allowed",
//             }}
//           >
//             {step === ONBOARDING_QUESTIONS.length - 1 ? "Finish →" : "Continue →"}
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// }

// ─── LOADING SCREEN ───────────────────────────────────────────────────────────

function LoadingScreen() {
  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Inter, sans-serif" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>⏳</div>
        <div style={{ fontSize: "0.88rem", color: "#64748b" }}>Loading your dashboard...</div>
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

export default function HomePage() {
  const navigate = useNavigate();
  const isMobile = useMobile();
  const [user, setUser]           = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [communityFeed, setCommunityFeed] = useState([]);
  const [solvedIds, setSolvedIds] = useState(new Set());
  const [loading, setLoading]     = useState(true);
  const badges = useBadges();
  usePageMeta({
    title: "Dashboard | Repractiq",
    description: "Track your SQL practice progress, streaks, badges and daily challenges on Repractiq.",
  });
  
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(0);
  const [onboardingAnswers, setOnboardingAnswers] = useState({ experience: null, goal: null, time: null });

  // ── Sign out ──────────────────────────────────────────────────────────────
  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/login");
  };
  // ── Complete onboarding ───────────────────────────────────────────────────
  const completeOnboarding = async (answers) => {
    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData?.session?.user?.id;
    if (!userId) return;

    const sqlPathMap = {
      beginner: "/sql/basics",
      basics: "/sql/intermediate",
      joins: "/sql/advanced",
      advanced: "/sql/interview",
      interview: "/sql/company",
    };
    
    const pythonPathMap = {
      beginner: "/python/basics",
      basics: "/python/intermediate",
      joins: "/python/advanced",
      advanced: "/python/interview",
      interview: "/python/company",
    };
    
    const pathMap =
      answers.tool === "python" ? pythonPathMap : sqlPathMap;
    
    const recommendedPath =
      pathMap[answers.experience] ||
      (answers.tool === "python" ? "/python" : "/sql/basics");

    await supabase.from("profiles").update({
      experience_level:    answers.experience,
      goal:                answers.goal,
      daily_time:          answers.time,
      recommended_path:    recommendedPath,
      onboarding_complete: true,    
    }).eq("id", userId);

    setShowOnboarding(false);
    navigate(recommendedPath);
  };

  // ── Main data fetch ───────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      // 1. Check auth
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData?.session) {
        navigate("/login");
        return;
      }
      const authUser = sessionData.session.user;
      const userId   = authUser.id;

      // 2. Fetch profile
      const { data: profile } = await supabase
        .from("profiles")
        .select("full_name, email, country, state, onboarding_complete")
        .eq("id", userId)
        .maybeSingle();
        if (!profile?.onboarding_complete) {
          setShowOnboarding(true);
        }

      // 3. Fetch this user's submissions
      const { data: mySubmissions } = await supabase
        .from("submissions")
        .select("id, problem_id, category, problem_title, status, time_taken_seconds, created_at, updated_at, query")
        .eq("user_id", userId)
        .order("updated_at", { ascending: false });

      const subs = mySubmissions || [];

      // Correct submissions only
      const correctSubs   = subs.filter((s) => s.status === "correct");
      const solvedIdSet   = new Set(correctSubs.map((s) => getSubmissionKey(s.category, s.problem_id)));
      setSolvedIds(solvedIdSet);

      // Weekly / last-week counts (based on updated_at of correct submissions)
      const weeklyCount   = correctSubs.filter((s) => isThisWeek(s.updated_at)).length;
      const lastWeekCount = correctSubs.filter((s) => isLastWeek(s.updated_at)).length;

      // Accuracy: unique problems attempted vs unique problems correct
      const attemptedIdSet = new Set(
        subs.map((s) => getSubmissionKey(s.category, s.problem_id))
      );
      
      const totalAttempts = attemptedIdSet.size;
      const correctCount = solvedIdSet.size;
      // Fast solves: correct problems solved in under 120s
      const fastSolves = correctSubs.filter((s) => (s.time_taken_seconds || 999) < 120).length;

      // Last problem worked on
      const lastSub     = subs[0];
      const lastProblem = lastSub
      ? {
          title:      lastSub.problem_title || "Unnamed problem",
          path:       getSubmissionProblemPath(lastSub.category, lastSub.problem_id),
          category:   lastSub.category,
          difficulty: "Easy",
        }
      : null;

      // 4. Fetch streak for this user
      const { data: streakRow } = await supabase
        .from("user_streaks")
        .select("current_streak, longest_streak, last_solved_date")
        .eq("user_id", userId)
        .maybeSingle();

      // 5. Fetch leaderboard: all users' solved counts + streaks
      //    We join profiles + submissions (correct) + user_streaks
      //    Strategy: fetch all correct submissions grouped by user_id,
      //    then fetch streaks, then merge with profiles for names.

      const { data: allCorrect } = await supabase
        .from("submissions")
        .select("user_id, problem_id")
        .eq("status", "correct");

      // Count unique problems solved per user
      const userSolvedMap = {};
      (allCorrect || []).forEach(({ user_id, problem_id }) => {
        if (!userSolvedMap[user_id]) userSolvedMap[user_id] = new Set();
        userSolvedMap[user_id].add(problem_id);
      });

      const { data: allStreaks } = await supabase
        .from("user_streaks")
        .select("user_id, current_streak, longest_streak");

        const streakLookup = {};

(allStreaks || []).forEach((s) => {
  streakLookup[s.user_id] = s.current_streak || 0;
});

      const { data: allProfiles } = await supabase
        .from("profiles")
        .select("id, full_name");

      const profileMap = {};
      (allProfiles || []).forEach((p) => { profileMap[p.id] = p.full_name || "Anonymous"; });

      const streakMap = {};
      (allStreaks || []).forEach((s) => { streakMap[s.user_id] = s.current_streak || 0; });

      // All user IDs that have at least one correct submission
      const allUserIds = Object.keys(userSolvedMap);

      const board = allUserIds.map((uid) => ({
        userId:   uid,
        fullName: profileMap[uid] || "Anonymous",
        solved:   userSolvedMap[uid]?.size || 0,
        streak:   streakMap[uid] || 0,
        score:    (userSolvedMap[uid]?.size || 0) + (streakMap[uid] || 0),
      }));

      board.sort((a, b) => b.score - a.score);
      setLeaderboard(board);

      // 6. Community feed: 6 most recent correct submissions across all users
      const { data: recentCorrect } = await supabase
      .from("submissions")
      .select(`id, user_id, problem_title, category, created_at, time_taken_seconds`)
      .eq("status", "correct")
      .order("created_at", { ascending: false })
      .limit(50); // ← was 10
    
    const uniqueFeed = [];
    const seen = new Set();
    
    (recentCorrect || []).forEach((row) => {
      if (!seen.has(row.user_id)) { // ← was the combined key
        seen.add(row.user_id);
        uniqueFeed.push(row);
      }
    });
      
      const feedWithNames = uniqueFeed.map((row) => ({
        ...row,
        fullName: profileMap[row.user_id] || "Anonymous",
        streak: streakLookup[row.user_id] || 0,
      }));
      
      setCommunityFeed(feedWithNames);

      // 7. Build user object
      setUser({
        userId:       userId,
        fullName:     profile?.full_name || authUser.email?.split("@")[0] || "User",
        solvedCount:  correctCount,
        streak:       streakRow?.current_streak  || 0,
        longestStreak: streakRow?.longest_streak || 0,
        weeklyCount,
        lastWeekCount,
        totalAttempts,
        correctCount,
        fastSolves,
        lastProblem,
      });

      setLoading(false);
    };

    load();
  }, [navigate]);

  if (loading) return <LoadingScreen />;

  const sqlCounts =
    (SQL_PROBLEMS?.length || 0) +
    (SQL_INTERMEDIATE_PROBLEMS?.length || 0) +
    (SQL_ADVANCED_PROBLEMS?.length || 0) +
    (SQL_INTERVIEW_PROBLEMS?.length || 0) +
    (SQL_SCENARIOS_PROBLEMS?.length || 0) +
    (SQL_COMPANY_PROBLEMS?.length || 0);

  const isNewUser = (user.solvedCount || 0) === 0;

  return (
    <div style={{ background: "#ffffff", minHeight: "100vh", fontFamily: "Inter, -apple-system, sans-serif", color: "#0f172a" }}>
      <Nav user={user} navigate={navigate} onSignOut={handleSignOut} isMobile={isMobile} />
      <HeroGreeting user={user} navigate={navigate} isMobile={isMobile} />
      {showOnboarding && (
        <OnboardingModal
          step={onboardingStep}
          answers={onboardingAnswers}
          setAnswers={setOnboardingAnswers}
          setStep={setOnboardingStep}
          onFinish={completeOnboarding}
          isMobile={isMobile}
        />
      )}

      <div style={{ maxWidth: "1100px", margin: "0 auto", padding: isMobile ? "1rem" : "2rem 2.5rem" }}>
        {isNewUser ? (
          <EmptyState navigate={navigate} />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>

         {/* Row 1: Left content + Right badges */}
<div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 340px", gap: "1.25rem", alignItems: "start" }}>
  
  {/* LEFT: Daily + Resume + Progress stacked */}
  <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
    <Section label="Daily challenge" title="">
      <DailyChallenge challenge={DAILY_CHALLENGE} navigate={navigate} solvedIds={solvedIds} />
    </Section>
    <Section label="Resume" title="">
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <ContinueCard problem={user.lastProblem} navigate={navigate} />
        <ProgressFeedback user={user} />
      </div>
    </Section>
  </div>

  {/* RIGHT: Badges tall sidebar */}
  <Section label="Your badges" title="">
    <BadgesCard user={user} badges={badges} navigate={navigate} />
  </Section>

</div>

            {/* Row 2: Practice categories */}
            <Section label="Practice" title="What do you want to work on?" action="All categories" actionFn={() => navigate("/sql")}>
              <PracticeCategories navigate={navigate} sqlCounts={sqlCounts} isMobile={isMobile} />
            </Section>

            {/* Row 3: Recommended + Leaderboard */}
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 340px", gap: "1.25rem", alignItems: "start" }}>
              <Section label="Recommended for you" title="Problems to try next" action="Browse all" actionFn={() => navigate("/sql/basics")}>
                <RecommendedProblems navigate={navigate} solvedIds={solvedIds} isMobile={isMobile} />
              </Section>
              <Section label="Leaderboard" title="">
                <LeaderboardCard currentUser={user} leaderboard={leaderboard} />
              </Section>
            </div>

            {/* Row 4: Community feed + Badges */}
            <Section label="Community" title="People are solving problems right now" action="View all" actionFn={() => navigate("/sql")}>
  <CommunityFeed feed={communityFeed} />
</Section>

          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ background: "#f8fafc", borderTop: "1px solid #e2e8f0", padding: isMobile ? "2rem 1rem 1.5rem" : "3rem 2.5rem 2rem", marginTop: "2rem" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr 1fr", gap: "2rem", marginBottom: "2rem" }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: "1rem", color: "#0f172a", marginBottom: "0.5rem" }}>Repractiq</div>
              <div style={{ fontSize: "0.82rem", color: "#64748b", lineHeight: 1.7, maxWidth: "280px" }}>A free SQL practice platform built for data professionals who want to actually do the work.</div>
              <div style={{ display: "flex", gap: "10px", marginTop: "1rem" }}>
                <a href="https://linkedin.com" target="_blank" rel="noreferrer" style={{ fontSize: "0.78rem", color: "#2563eb", fontWeight: 600, textDecoration: "none" }}>LinkedIn</a>
                <a href="https://youtube.com"  target="_blank" rel="noreferrer" style={{ fontSize: "0.78rem", color: "#2563eb", fontWeight: 600, textDecoration: "none" }}>YouTube</a>
                <a href="/contact" style={{ fontSize: "0.78rem", color: "#2563eb", fontWeight: 600, textDecoration: "none" }}>Contact Us</a>
              </div>
            </div>
            <div>
            <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#0f172a", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "0.75rem" }}>Product</div>
              {[["Practice", "/sql"], ["Leaderboard", "/leaderboard"], ["Community", "/blog"]].map(([link, path]) => (
                <div key={link} onClick={() => navigate(path)} style={{ fontSize: "0.82rem", color: "#64748b", marginBottom: "0.4rem", cursor: "pointer" }}>{link}</div>
              ))}
            </div>
            <div>
  <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#0f172a", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "0.75rem" }}>
    Legal
  </div>
  
  <Link 
    to="/privacy" 
    style={{ display: "block", fontSize: "0.82rem", color: "#64748b", marginBottom: "0.4rem", textDecoration: "none", cursor: "pointer" }}
  >
    Privacy Policy
  </Link>

  <Link 
    to="/terms" 
    style={{ display: "block", fontSize: "0.82rem", color: "#64748b", marginBottom: "0.4rem", textDecoration: "none", cursor: "pointer" }}
  >
    Terms of Use
  </Link>
</div>
          </div>
          <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: "1.25rem", fontSize: "0.75rem", color: "#94a3b8", textAlign: "center" }}>
            © 2026 Repractiq · Built for data professionals who want to actually do the work.
          </div>
        </div>
      </div>
      <BadgeUnlockModal
  badges={badges.newlyUnlocked}
  isOpen={badges.newlyUnlocked.length > 0}
  onClose={badges.clearNewlyUnlocked}
  onViewBadges={() => navigate("/profile")}
  isMobile={isMobile}
/>
    </div>
  );
}